/**
 * Turns the raw System snapshot into a list of named checks with a status.
 * Pure: the thresholds are constants below and every check states what it saw.
 * A check that cannot be evaluated is "unknown", never "ok".
 */

import { formatDuration, formatNumber } from "../dashboard/insights/format";
import type { SystemDto } from "@/modules/dashboard/insights/types";

export type HealthStatus = "ok" | "warn" | "bad" | "unknown";

export interface HealthCheck {
  id: string;
  area: string;
  label: string;
  status: HealthStatus;
  detail: string;
  href?: string;
}

/** Slow but working, and broken. Round trip to the database from the API. */
export const DB_SLOW_MS = 1000;
export const DB_VERY_SLOW_MS = 3000;
export const EVENT_BACKLOG_WARN_SECONDS = 10 * 60;
export const MEDIA_QUEUE_WARN_SECONDS = 60 * 60;
export const MEDIA_QUEUE_BAD_SECONDS = 24 * 60 * 60;

/**
 * Tables that prove a recent migration reached this database. The migration
 * ledger itself is not reliable here (it lags the live schema), so the schema
 * is checked by the objects the migrations create.
 */
export const SCHEMA_MARKERS: { migration: string; table: string; what: string }[] = [
  { migration: "i44", table: "message_hides", what: "Delete for me in chat" },
  { migration: "i47", table: "message_reactions", what: "Emoji reactions in chat" },
  { migration: "i49", table: "worker_heartbeat", what: "Background job heartbeats" },
];

const RANK: Record<HealthStatus, number> = { ok: 0, unknown: 1, warn: 2, bad: 3 };

export function worstStatus(checks: readonly HealthCheck[]): HealthStatus {
  return checks.reduce<HealthStatus>((worst, c) => (RANK[c.status] > RANK[worst] ? c.status : worst), "ok");
}

export function buildHealthChecks(s: SystemDto): HealthCheck[] {
  const checks: HealthCheck[] = [];

  // API and database ---------------------------------------------------------
  const ms = s.api.db_round_trip_ms;
  checks.push({
    id: "db-latency",
    area: "API and database",
    label: "Database round trip",
    status: ms >= DB_VERY_SLOW_MS ? "bad" : ms >= DB_SLOW_MS ? "warn" : "ok",
    detail: `${formatNumber(ms)} ms from the API to the database and back, including this snapshot query.`,
  });

  // Background jobs ----------------------------------------------------------
  const hb = s.worker_heartbeats;
  if (hb.length === 0) {
    checks.push({
      id: "cron",
      area: "Background jobs",
      label: "Cron heartbeat",
      status: "unknown",
      detail:
        "No background job has reported yet. Heartbeats are written by the scheduled job once the API build that includes them is deployed and the database migration i49 is applied.",
    });
  } else {
    const quiet = hb.filter((w) => w.quiet);
    const failing = hb.filter((w) => w.last_failed > 0 || w.last_error);
    checks.push({
      id: "cron",
      area: "Background jobs",
      label: "Cron heartbeat",
      status: quiet.length > 0 ? "bad" : failing.length > 0 ? "warn" : "ok",
      detail:
        quiet.length > 0
          ? `${quiet.map((w) => w.worker).join(", ")} ${quiet.length === 1 ? "has" : "have"} not reported for more than ${formatDuration(s.api.quiet_after_seconds)}. The scheduled job may have stopped.`
          : failing.length > 0
            ? `${failing.map((w) => w.worker).join(", ")} reported errors on the last run.`
            : `All ${hb.length} jobs reported within ${formatDuration(s.api.quiet_after_seconds)}.`,
    });
  }

  // Events and integrations --------------------------------------------------
  const ev = s.domain_events;
  const backlogAge = ev.oldest_undelivered_at
    ? Math.max(0, Math.round((Date.now() - new Date(ev.oldest_undelivered_at).getTime()) / 1000))
    : 0;
  checks.push({
    id: "domain-events",
    area: "Events",
    label: "Domain event delivery",
    status:
      ev.failed + ev.quarantined > 0
        ? "bad"
        : ev.undelivered > 0 && backlogAge > EVENT_BACKLOG_WARN_SECONDS
          ? "warn"
          : "ok",
    detail:
      ev.failed + ev.quarantined > 0
        ? `${formatNumber(ev.failed)} failed and ${formatNumber(ev.quarantined)} quarantined events.`
        : ev.undelivered > 0
          ? `${formatNumber(ev.undelivered)} events waiting; the oldest for ${formatDuration(backlogAge)}.`
          : "Every recorded event has been delivered.",
  });

  const ie = s.integration_events;
  checks.push({
    id: "integration-events",
    area: "Events",
    label: "Integration events",
    status: ie.failed_24h > 0 ? "bad" : ie.failed > 0 || ie.stuck_processing > 0 ? "warn" : "ok",
    detail:
      ie.failed_24h > 0
        ? `${formatNumber(ie.failed_24h)} failed in the last 24 hours (${formatNumber(ie.failed)} in total).`
        : ie.failed > 0
          ? `${formatNumber(ie.failed)} failed earlier; none in the last 24 hours.`
          : ie.stuck_processing > 0
            ? `${formatNumber(ie.stuck_processing)} still marked processing after more than 10 minutes. They will not finish on their own.`
            : "No failed or stuck integration events.",
    href: "/app/system/integration-events",
  });

  // Payments -----------------------------------------------------------------
  const pay = s.config.payments;
  const ing = s.ingress;
  checks.push({
    id: "payments",
    area: "Payments",
    label: "Xendit payments",
    status: !pay.configured ? "warn" : ing.rejected_7d > 0 ? "warn" : "ok",
    detail: !pay.configured
      ? "Xendit is not configured on this API, so checkout fails closed and no payment can be taken."
      : `Configured with a ${pay.mode} key.${
          ing.last_received_at ? "" : " No webhook has been received from Xendit yet."
        }${ing.rejected_7d > 0 ? ` ${formatNumber(ing.rejected_7d)} webhook${ing.rejected_7d === 1 ? " was" : "s were"} rejected in the last 7 days.` : ""}`,
  });

  // Queues -------------------------------------------------------------------
  const md = s.media_deletions;
  checks.push({
    id: "media-queue",
    area: "Storage",
    label: "Media deletion queue",
    status:
      md.depth === 0
        ? "ok"
        : (md.oldest_age_seconds ?? 0) > MEDIA_QUEUE_BAD_SECONDS
          ? "bad"
          : (md.oldest_age_seconds ?? 0) > MEDIA_QUEUE_WARN_SECONDS
            ? "warn"
            : "ok",
    detail:
      md.depth === 0
        ? "Nothing waiting to be removed from storage."
        : `${formatNumber(md.depth)} files waiting; the oldest for ${formatDuration(md.oldest_age_seconds ?? 0)}.`,
  });

  checks.push({
    id: "file-assets",
    area: "Storage",
    label: "File records",
    status: s.files.quarantined > 0 || s.files.stuck_pending_upload > 0 ? "warn" : "ok",
    detail:
      s.files.quarantined > 0 || s.files.stuck_pending_upload > 0
        ? `${formatNumber(s.files.quarantined)} quarantined and ${formatNumber(s.files.stuck_pending_upload)} uploads that never finished.`
        : `${formatNumber(s.files.total)} file records; none quarantined or stuck.`,
  });

  checks.push({
    id: "notifications",
    area: "Notifications",
    label: "Notification delivery",
    status: s.notification_delivery.failed > 0 ? "warn" : "ok",
    detail:
      s.notification_delivery.failed > 0
        ? `${formatNumber(s.notification_delivery.failed)} deliveries failed, ${formatNumber(s.notification_delivery.retrying)} retrying.`
        : "No failed deliveries.",
  });

  // Bookings machinery -------------------------------------------------------
  checks.push({
    id: "holds",
    area: "Bookings",
    label: "Booking hold expiry",
    status: s.holds.overdue > 0 ? "bad" : "ok",
    detail:
      s.holds.overdue > 0
        ? `${formatNumber(s.holds.overdue)} holds are past their expiry and still active, so those slots stay blocked. The hold-expiry job is not releasing them.`
        : `${formatNumber(s.holds.live)} live holds, none overdue.`,
    href: "/app/bookings/holds",
  });

  checks.push({
    id: "idempotency",
    area: "Requests",
    label: "Requests stuck in progress",
    status: s.idempotency.stuck_in_progress > 0 ? "warn" : "ok",
    detail:
      s.idempotency.stuck_in_progress > 0
        ? `${formatNumber(s.idempotency.stuck_in_progress)} requests began more than 10 minutes ago and never completed. Retrying one with the same key is refused until it is cleared.`
        : "No request is stuck in progress.",
  });

  // Configuration ------------------------------------------------------------
  for (const c of s.config.checks) {
    if (c.key === "xendit") continue; // reported under Payments
    checks.push({
      id: `config-${c.key}`,
      area: "Configuration",
      label: c.label,
      status: c.ok ? "ok" : "bad",
      detail: c.detail,
    });
  }

  // Schema -------------------------------------------------------------------
  const tables = new Set(s.database.table_names);
  const missing = SCHEMA_MARKERS.filter((m) => !tables.has(m.table));
  checks.push({
    id: "schema",
    area: "Database schema",
    label: "Recent migrations present",
    status: missing.length > 0 ? "bad" : "ok",
    detail:
      missing.length > 0
        ? `Missing: ${missing.map((m) => `${m.migration} (${m.what})`).join(", ")}. The database is behind the code.`
        : `Checked ${SCHEMA_MARKERS.length} recent migrations by the tables they create; all present.`,
  });

  return checks;
}
